import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQuery } from './createBaseQuery';

// Интерфейс для пакета токенов
export interface ITokenPackage {
  id?: string;
  name: string;
  tokensAmount: number;
  price: number;
  strikethroughPrice?: number;
  discountPriceOffer?: string;
  bonusTokenOffer?: string;
  currency: string;
  isActive?: boolean;
  sortOrder?: number;
  externalId?: string;
  createdAt?: Date;
  ab_test?: string;
}

// Интерфейс для настроек токенов
export interface ITokenSettings {
  signupBonus: number;
  monthlySubscriptionInitialBonus: number;
  monthlySubscriptionBonus: number;
  yearlySubscriptionInitialBonus: number;
  yearlySubscriptionMonthlyBonus: number;
  photoGenerationCost: number;
}

export interface ITokenCosts {
  photoGenerationCost: number;
  dialogPhotoGenerationCost: number;
  avatarGenerationCost: number;
  hireWriterCost: number;
  dialogVideoGenerationCost: number;
}

export const tokenApi = createApi({
  reducerPath: 'tokenApi',
  baseQuery: createBaseQuery(),
  endpoints: (builder) => ({
    getTokenPrices: builder.query<{ costs: ITokenCosts; packages: ITokenPackage[] }, void>({
      query: () => ({
        url: '/token-prices',
        method: 'GET',
      }),
    }),

    getTokenCosts: builder.query<ITokenCosts, void>({
      query: () => ({
        url: '/token-prices',
        method: 'GET',
      }),
      transformResponse: (response: { costs: ITokenCosts; packages: ITokenPackage[] }) => response.costs,
    }),

    createPaymentSession: builder.mutation<{ paymentGatewayUrl: string }, { external_id: string }>({
      query: (body) => ({
        url: '/trustpay/package-payment-session-3ds',
        method: 'POST',
        body,
      }),
    }),

    createSilentTokenPurchase: builder.mutation<{ success: boolean }, { external_id: string }>({
      query: (body) => ({
        url: '/trustpay/silent-token-purchase',
        method: 'POST',
        body,
      }),
    }),

    // Административные эндпоинты
    getTokenSettings: builder.query<ITokenSettings, void>({
      query: () => ({
        url: '/admin/token-settings',
        method: 'GET',
      }),
    }),

    createTokenSettings: builder.mutation<void, ITokenSettings>({
      query: (body) => ({
        url: '/admin/token-settings',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
    }),

    updateTokenSettings: builder.mutation<void, { id: string; params: ITokenSettings }>({
      query: ({ id, params }) => ({
        url: '/admin/token-settings',
        method: 'PUT',
        body: params,
        requiresAuth: true,
        searchParams: { id },
      }),
    }),

    getTokenPackages: builder.query<ITokenPackage[], void>({
      query: () => ({
        url: '/admin/token-packages',
        method: 'GET',
      }),
    }),

    createTokenPackage: builder.mutation<void, ITokenPackage>({
      query: (body) => ({
        url: '/admin/token-packages',
        method: 'POST',
        body,
      }),
    }),

    updateTokenPackage: builder.mutation<void, { id: string; params: ITokenPackage }>({
      query: ({ id, params }) => ({
        url: `/admin/token-packages`,
        method: 'PUT',
        body: params,
        requiresAuth: true,
        searchParams: { id },
      }),
    }),

    deleteTokenPackage: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/admin/token-packages`,
        method: 'DELETE',
        requiresAuth: true,
        searchParams: { id },
      }),
    }),
  }),
});

export const {
  useGetTokenPricesQuery,
  useGetTokenCostsQuery,
  useCreatePaymentSessionMutation,
  useCreateSilentTokenPurchaseMutation,
  useGetTokenSettingsQuery,
  useCreateTokenSettingsMutation,
  useUpdateTokenSettingsMutation,
  useGetTokenPackagesQuery,
  useCreateTokenPackageMutation,
  useUpdateTokenPackageMutation,
  useDeleteTokenPackageMutation,
} = tokenApi;
